import Image, { ImageProps } from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { BLOG_NAV_HEIGHT } from '@/components/navigation/BlogNavigation';
import Video from '@/components/Video';
import YouTubeVideo from '@/components/YouTubeVideo';
import gtm from '@/lib/gtm';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { extractYouTubeId } from '@/lib/youtube';
import { COLORS } from '@/styles/color';
import { marginY, mediaBreakpointUp, paddingX, paddingY, square } from '@/styles/layout/utils';
import { FONT_FAMILIES, FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { decode } from 'html-entities';
import { Parser } from 'html-to-react';
import Script from 'next/script';
import React, { FC } from 'react';
import styled from 'styled-components';

const htmlToReactParser = new Parser();

// #region - Components

const Wrapper = styled(Container)`
  padding-top: ${remCalc(BLOG_NAV_HEIGHT)};
`;

interface ParagraphProps {
  textAlign?: VizyTextAlign;
}

const Paragraph = styled.p<ParagraphProps>`
  ${customTextSize(14, 20)};
  text-align: ${(props) => props.textAlign ?? 'left'};
  margin-bottom: ${remCalc(20)};
  white-space: pre-line;

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(18, 26)};
    margin-bottom: ${remCalc(30)};
  }
`;

const Highlight = styled.span`
  background-color: var(--article-accent-color-highlight);
`;

const TextLink = styled.a`
  color: ${COLORS.BLUE.LOWLIGHT};

  &:hover {
    color: ${COLORS.BLACK};
  }
`;

const Strikethrough = styled.span`
  text-decoration: line-through;
`;

const InlineCode = styled.code`
  ${customTextSize(16, 24)};
  ${paddingX(5)}
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(5)};
`;

const CodeBlock = styled.pre`
  ${customTextSize(16, 24)};
  ${paddingX(20)}
  ${paddingY(15)}
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: ${COLORS.BLACK};
  color: ${COLORS.WHITE};
  border-radius: ${remCalc(5)};
  margin-bottom: ${remCalc(32)};
`;

const Heading2 = styled.h2`
  ${customTextSize(32, 38)};
  font-weight: ${FONT_WEIGHTS.NORMAL};
  margin-bottom: ${remCalc(26)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(49, 58)};
    margin-bottom: ${remCalc(40)};
  }
`;

const Heading3 = styled.h3`
  ${customTextSize(24, 30)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 38)};
    margin-bottom: ${remCalc(32)};
  }
`;

const Heading4 = styled.h4`
  ${customTextSize(16, 21)};
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-bottom: ${remCalc(22)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 24)};
    margin-bottom: ${remCalc(32)};
  }
`;

const Heading5 = styled.h5`
  // TODO
`;

const Heading6 = styled.h6`
  ${customTextSize(12, 20)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  text-transform: uppercase;
  margin-bottom: ${remCalc(16)};
  letter-spacing: 0.08rem;

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(16, 20)};
    font-weight: ${FONT_WEIGHTS.BOLD};
    margin-bottom: ${remCalc(33)};
  }
`;

const ListItem = styled.li`
  display: flex;
  gap: ${remCalc(10)};

  & ${Paragraph} {
    margin-bottom: unset;
    ${customTextSize(14, 20)};
    & strong {
      font-weight: ${FONT_WEIGHTS.NORMAL};
    }
  }

  ${mediaBreakpointUp('tablet')} {
    & ${Paragraph} {
      ${customTextSize(18, 26)};
    }
    gap: ${remCalc(13)};
  }

  ${mediaBreakpointUp('desktop')} {
    gap: ${remCalc(12)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    gap: ${remCalc(16)};
  }
`;

const UnorderedList = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(16)};
  margin-bottom: ${remCalc(20)};

  ${ListItem} {
    &::before {
      content: '';
      ${square(24)};
      background-image: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='16' cy='16' r='16' fill='%23001B38'/%3E%3Cpath d='M9 16.1176L13.9412 21.0588L23 12' stroke='white' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'/%3E%3C/svg%3E%0A");
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      flex-shrink: 0;

      ${mediaBreakpointUp('tablet')} {
        ${square(32)}
      }
    }
  }

  ${mediaBreakpointUp('tablet')} {
    row-gap: ${remCalc(20)};
    margin-bottom: ${remCalc(30)};
  }
`;

const OrderedList = styled.ol`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(16)};
  margin-bottom: ${remCalc(20)};

  counter-reset: olCounter ${(props) => Math.max((props.start ?? 0) - 1, 0)};

  ${ListItem} {
    counter-increment: olCounter 1;
    position: relative;

    &::before {
      content: counter(olCounter);
      ${square(24)};
      background-color: ${COLORS.BLACK};
      border-radius: 100%;
      color: ${COLORS.WHITE};
      ${customTextSize(12, 0)}
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      ${mediaBreakpointUp('tablet')} {
        ${square(32)};
        ${customTextSize(14, 0)}
      }
    }
  }

  ${mediaBreakpointUp('tablet')} {
    row-gap: ${remCalc(20)};
    margin-bottom: ${remCalc(30)};
  }
`;

const HorizontalRule = styled.hr`
  ${marginY(32)};
`;

const Blockquote = styled.figure`
  ${marginY(48)}

  blockquote {
    position: relative;
    ${customTextSize(28, 36)};
    font-family: ${FONT_FAMILIES.SERIF};
    font-weight: ${FONT_WEIGHTS.MEDIUM};

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(40, 52)};
    }

    &::before {
      content: '“';
      position: absolute;
      left: ${remCalc(-16)};
      top: 0;

      ${mediaBreakpointUp('desktop')} {
        left: ${remCalc(-22)};
      }
    }

    &::after {
      content: '”';
      margin-left: ${remCalc(8)};
    }

    p {
      display: inline;
    }
  }

  figcaption {
    margin-top: ${remCalc(24)};
  }
`;

const Figure = styled.figure`
  ${marginY(48)}

  figcaption {
    ${customTextSize(16, 24)};
    font-style: italic;
    margin-top: ${remCalc(8)};
  }
`;

const StyledYouTubeVideo = styled(YouTubeVideo)`
  ${marginY(48)}
`;

const StyledVideo = styled(Video)`
  ${marginY(48)}
`;

type StyledImageProps = ImageProps & {
  rounded: boolean;
};

const StyledImage = styled<FC<StyledImageProps>>(Image)`
  border-radius: ${(props) => (props.rounded ? remCalc(10) : 0)};
  overflow: hidden;
`;

const Tweet = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-bottom: ${remCalc(32)};
`;

const Slideshare = styled.div`
  margin-bottom: ${remCalc(32)};
`;

const LinkedIn = styled.div`
  margin-bottom: ${remCalc(32)};
`;

// #endregion - Components

const parseVizyNode = (node: VizyNode, index: number, nodeDetail?: any) => {
  if (node.type === 'text') {
    node.text = decode(node.text);

    return parseVizyMarks(node.marks, node.text);
  }

  let content: React.ReactNode[] = [];

  if (node.content) {
    content = node.content?.map((node, index) => parseVizyNode(node, index));
  }

  if (node.type === 'paragraph') {
    return <Paragraph textAlign={node.attrs?.textAlign}>{!!content.length ? content : '\u00A0'}</Paragraph>;
  } else if (node.type === 'codeBlock') {
    return <CodeBlock>{content}</CodeBlock>;
  } else if (node.type === 'hardBreak') {
    return <br />;
  } else if (node.type === 'heading') {
    switch (node.attrs?.level) {
      case 2:
        return <Heading2>{content}</Heading2>;
      case 3:
        return <Heading3>{content}</Heading3>;
      case 4:
        return <Heading4>{content}</Heading4>;
      case 5:
        return <Heading5>{content}</Heading5>;
      default:
        return <Heading6>{content}</Heading6>;
    }
  } else if (node.type === 'horizontalRule') {
    return <HorizontalRule />;
  } else if (node.type === 'orderedList') {
    return <OrderedList start={node.attrs?.start}>{content}</OrderedList>;
  } else if (node.type === 'bulletList') {
    return <UnorderedList>{content}</UnorderedList>;
  } else if (node.type === 'listItem') {
    return (
      <ListItem>
        <Paragraph>{content}</Paragraph>
      </ListItem>
    );
  } else if (node.type === 'vizyBlock') {
    switch (nodeDetail?.blockTypeHandle) {
      case 'blockquoteLinked':
        return nodeDetail?.associatedQuotes?.map((quote, i) => (
          <Blockquote key={quote.quote ?? i}>
            <blockquote>{htmlToReactParser.parse(quote.quote)}</blockquote>

            {quote.authorName && (
              <figcaption>
                <span>– {quote.authorName}</span>
                {quote.authorTitle && (
                  <span>
                    , <strong>{quote.authorTitle}</strong>
                  </span>
                )}
              </figcaption>
            )}
          </Blockquote>
        ));
      case 'blockquoteStandalone':
        return (
          <Blockquote>
            <blockquote>{htmlToReactParser.parse(nodeDetail?.quote)}</blockquote>

            {nodeDetail?.authorName && (
              <figcaption>
                <span>– {nodeDetail?.authorName}</span>
                {nodeDetail?.authorTitle && (
                  <span>
                    , <strong>{nodeDetail?.authorTitle}</strong>
                  </span>
                )}
              </figcaption>
            )}
          </Blockquote>
        );
      case 'image':
        const image = nodeDetail?.image?.[0];

        if (!image) return null;

        return (
          <Figure>
            <StyledImage
              src={image.url}
              width={image.width}
              height={image.height}
              alt={image.altText}
              rounded={nodeDetail?.hasRoundedCorners}
            />

            {nodeDetail?.imageCaption && <figcaption>{nodeDetail?.imageCaption}</figcaption>}
          </Figure>
        );
      case 'youtubeVideo':
        const youtubeVideoUrl = nodeDetail?.youtubeVideoUrl;

        if (!youtubeVideoUrl) return null;

        const youtubeVideoId = extractYouTubeId(youtubeVideoUrl);

        return (
          <StyledYouTubeVideo
            videoId={youtubeVideoId!}
            coverSrc={nodeDetail?.coverImage?.[0]?.url!}
            onInitialPlay={() => {
              gtm.track('play_video', {
                video_title: maybeNotSet(nodeDetail?.videoLabel),
                video_url: maybeNotSet(youtubeVideoUrl),
                video_youtube_id: maybeNotSet(youtubeVideoId),
              });
            }}
          />
        );
      case 'videoAsset':
        const videoUrl = nodeDetail?.videoAsset?.[0]?.url;

        if (!videoUrl) return null;

        return (
          <StyledVideo
            videoUrl={videoUrl}
            onInitialPlay={() => {
              gtm.track('play_video', {
                video_title: maybeNotSet(nodeDetail?.videoLabel),
                video_url: maybeNotSet(videoUrl),
              });
            }}
          />
        );
      case 'twitter':
        if (!nodeDetail.embedCode) return null;

        const html = nodeDetail.embedCode.replace(/<script\s.+?<\/script>/gim, '');

        return (
          <>
            <Tweet>{htmlToReactParser.parse(html)}</Tweet>
            <Script src="https://platform.twitter.com/widgets.js" key="twitter-widgets" strategy="lazyOnload" />
          </>
        );
      case 'slideshare':
        const ssmatches = (nodeDetail.embedCode as string).matchAll(/embed_code\/key\/(\w+)\W/gim);

        return [...ssmatches].map((match) => {
          if (!match[1]) return null;

          return (
            <Slideshare key={match[1]}>
              <iframe
                src={`//www.slideshare.net/slideshow/embed_code/key/${match[1]}`}
                width="100%"
                height={500}
                frameBorder={0}
                marginWidth={0}
                marginHeight={0}
                scrolling="no"
                allowFullScreen
              />
            </Slideshare>
          );
        });
      case 'linkedin':
        const lmatches = (nodeDetail.embedCode as string).matchAll(
          /<iframe.+?src="(.+?linkedin\.com.+?)".+?<\/iframe>/gim,
        );

        return [...lmatches].map((match) => {
          if (!match[1]) return null;

          return (
            <LinkedIn key={match[1]}>
              <iframe src={match[1]} width="100%" height={500} frameBorder={0} marginWidth={0} marginHeight={0} />
            </LinkedIn>
          );
        });
      default:
        return null;
    }
  }

  return null;
};

const parseVizyMarks = (marks: VizyMark[] | undefined, text: string) => {
  if (!marks) return text;

  return marks.reduce((content, mark) => {
    if (mark.type === 'bold') {
      return <strong>{content}</strong>;
    } else if (mark.type === 'italic') {
      return <em>{content}</em>;
    } else if (mark.type === 'underline') {
      return <u>{content}</u>;
    } else if (mark.type === 'highlight') {
      return <Highlight>{content}</Highlight>;
    } else if (mark.type === 'strike') {
      return <Strikethrough>{content}</Strikethrough>;
    } else if (mark.type === 'code') {
      return <InlineCode>{content}</InlineCode>;
    } else if (mark.type === 'subscript') {
      return <sub>{content}</sub>;
    } else if (mark.type === 'superscript') {
      return <sup>{content}</sup>;
    } else if (mark.type === 'link') {
      return (
        <Link href={mark.attrs?.href ?? '#'}>
          <TextLink
            target={mark.attrs?.target}
            onClick={() => {
              gtm.track('click', {
                element_type: 'link',
                element_label: maybeNotSet(text.toString()),
                location: 'Blog Article',
              });
            }}
          >
            {content}
          </TextLink>
        </Link>
      );
    }

    return content;
  }, text);
};

interface VizyBlogArticleProps {
  rawNodes: string;
  nodes: any;
}

const VizyBlogArticle: FC<VizyBlogArticleProps> = ({ rawNodes, nodes }) => {
  const parsedNodes: VizyNode[] = JSON.parse(rawNodes);

  return <>{parsedNodes.map((node, index) => parseVizyNode(node, index, nodes[index]))}</>;
};

export default VizyBlogArticle;
